import "../button/btn.css"
import "../input-text-base/index.css"
import "./index.css"

import Icon from "@utilities/Icon"
import type { CombineAriaPropsWithCustomProps } from "@utilities/reactAriaProps"
import classNames from "classnames"
import React from "react"
import {
  Button,
  Group,
  Input,
  Label,
  NumberField as AriaNumberField,
  type NumberFieldProps as AriaNumberFieldProps,
  Text,
} from "react-aria-components"

export type NumberStepperSize = "md" | "lg"

export interface NumberStepperProps {
  /** The default uncontrolled value. */
  defaultValue?: number
  /** Helper text displayed below the component. Styled as error text when `invalid` is `true`. */
  description?: string
  /** Disables the input and stepper buttons. */
  disabled?: boolean
  /** If `true`, renders the label visually hidden. */
  hideLabel?: boolean
  /** If `true`, applies error styling to the field and description. */
  invalid?: boolean
  /** Accessible label for the field. */
  label: string
  /** Maximum allowed value. */
  max?: number
  /** Minimum allowed value. */
  min?: number
  /** Allows the value to be read but not changed. */
  readOnly?: boolean
  /** If `true`, appends an asterisk after the label text. */
  required?: boolean
  /** The size of the number stepper. */
  size?: NumberStepperSize
}

type AriaNumberFieldPropsToOmit =
  | "children"
  | "render"
  | "slot"
  | "validate"
  | "validationBehavior"

type AriaNumberFieldPropsToInclude =
  | "formatOptions"
  | "name"
  | "onChange"
  | "step"
  | "value"

export type NumberStepperElementProps = CombineAriaPropsWithCustomProps<
  AriaNumberFieldProps,
  NumberStepperProps,
  AriaNumberFieldPropsToOmit,
  AriaNumberFieldPropsToInclude
>

/**
 * A number stepper component that pairs a numeric input with increment and
 * decrement buttons. Use it when users need to enter or adjust a number by
 * small amounts, such as quantities or counts.
 *
 * Constrain the input with `min`, `max`, and `step`, and customize display
 * (currency, percentage, decimal precision) via the `formatOptions` prop.
 *
 * @component
 * @see {@link https://planningcenter.github.io/tapestry/?path=/docs/components-numberstepper--docs | Storybook Documentation}
 */
export function NumberStepper({
  className,
  defaultValue,
  description,
  disabled,
  hideLabel,
  invalid,
  label,
  max,
  min,
  readOnly,
  required,
  size = "md",
  ...restProps
}: NumberStepperElementProps) {
  const combinedClassName = classNames(
    "tds-field tds-number-stepper",
    {
      "tds-field--lg": size === "lg",
      "tds-number-stepper--lg": size === "lg",
    },
    className
  )

  return (
    <AriaNumberField
      {...restProps}
      aria-label={hideLabel ? label : undefined}
      className={combinedClassName}
      defaultValue={defaultValue}
      isDisabled={disabled}
      isInvalid={invalid}
      isReadOnly={readOnly}
      isRequired={required}
      maxValue={max}
      minValue={min}
    >
      {!hideLabel && <Label className="tds-field-label">{label}</Label>}
      <Group className="tds-field-control tds-number-stepper-field">
        <Input className="tds-number-stepper-input" />
        <Button
          className="tds-btn tds-btn--infield tds-number-stepper-button"
          slot="decrement"
        >
          <Icon aria-hidden symbol="general#minus" />
        </Button>
        <Button
          className="tds-btn tds-btn--infield tds-number-stepper-button"
          slot="increment"
        >
          <Icon aria-hidden symbol="general#plus" />
        </Button>
      </Group>
      {description && (
        <Text className="tds-field-description" slot="description">
          <Icon
            aria-hidden
            className="tds-field-description-invalid-icon"
            symbol="general#exclamation-triangle"
          />
          {description}
        </Text>
      )}
    </AriaNumberField>
  )
}
